import { FloatingActions } from "@/components/floating-actions/floating-actions";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSTooltipPlacement,
  MDSIcon,
  MDSIconKind,
  MDSPanelResponsiveButton,
  MDSSpacer,
} from "@/mds";

export interface MultiSelectActionsProps {
  listState: ListStateObservable;
}

export const MultiSelectActions = observer<MultiSelectActionsProps>(({ listState }) => {
  const numberOfSelectedIds = listState.selectedItemIds.length;
  const isOpen = !!numberOfSelectedIds;

  // TODO: consider using 3rd party hooks for all shortcuts
  useEffect(() => {
    const onEscPressed = (event: KeyboardEvent) => {
      if (!listState?.isSelectModeActive) {
        return;
      }

      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      listState.reset();
    };

    document.addEventListener("keydown", onEscPressed);
    return () => {
      document.removeEventListener("keydown", onEscPressed);
    };
  }, [listState]);

  return (
    <FloatingActions isOpen={isOpen}>
      <DeselectButton variant={MDSButtonVariant.EmphasisLight} onClick={listState.clearSelection}>
        <span>{numberOfSelectedIds} selected</span>
        <MDSIcon kind={MDSIconKind.Exit} slot={MDSButtonSlotName.RightIcon} />
      </DeselectButton>
      <Box>
        <MDSSpacer />
        {listState.store.debug.debugModeEnabled && (
          <MDSPanelResponsiveButton
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="Copy"
            collapsedIcon={listState.isCopying ? MDSIconKind.Spinner : MDSIconKind.Copy}
            onClick={listState.handleCopySelectedNotes}
            isDisabled={listState.isCopying}
          />
        )}
        {listState.canAutoOrganize && (
          <ChromeActionPanelButton
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="Auto Organize"
            fullSizeIcon={MDSIconKind.AutoOrganize}
            collapsedIcon={MDSIconKind.AutoOrganize}
            onClick={listState.handleAutoOrganize}
            isDisabled={false}
            collapsedTooltipConfig={{
              label: "Let Mem organize your notes",
              placement: MDSTooltipPlacement.Top,
            }}
            tooltipConfig={{
              label: "Let Mem organize your notes",
              placement: MDSTooltipPlacement.Top,
            }}
          />
        )}
        {listState.canChatWithSelection && (
          <ChromeActionPanelButton
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="Chat"
            fullSizeIcon={MDSIconKind.Message}
            collapsedIcon={MDSIconKind.Message}
            onClick={listState.handleChatWithSelection}
            collapsedTooltipConfig={{
              label: "Open a chat with these notes",
              placement: MDSTooltipPlacement.Top,
            }}
            tooltipConfig={{
              label: "Open a chat with these notes",
              placement: MDSTooltipPlacement.Top,
            }}
          />
        )}
        {listState.canAddToCollection && (
          <MDSPanelResponsiveButton
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="# Organize"
            collapsedIcon={MDSIconKind.Collection}
            onClick={listState.handleAddToCollection}
          />
        )}
        {listState.canRemoveFromCollection && (
          <MDSPanelResponsiveButton
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="Remove from collection"
            collapsedIcon={MDSIconKind.Minus}
            onClick={listState.handleRemoveFromCollection}
          />
        )}
        {listState.canRestoreFromTrash && (
          <MDSPanelResponsiveButton
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            fullSizeLabel="Restore from trash"
            collapsedIcon={MDSIconKind.Redo}
            onClick={listState.handleRestore}
          />
        )}
        {listState.canMoveNoteToTrash && (
          <MDSPanelResponsiveButton
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            collapsedIcon={MDSIconKind.Trash}
            fullSizeIcon={MDSIconKind.Trash}
            onClick={listState.handleDeleteNote}
            fullSizeLabel="Move to trash"
          />
        )}
        {listState.canMoveCollectionToTrash && (
          <MDSPanelResponsiveButton
            threshold="575px"
            variant={MDSButtonVariant.Chrome}
            collapsedIcon={MDSIconKind.Trash}
            fullSizeIcon={MDSIconKind.Trash}
            onClick={listState.handleDeleteCollections}
            fullSizeLabel="Delete collection"
          />
        )}
      </Box>
    </FloatingActions>
  );
});

const Box = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.surface.chrome};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  gap: ${({ theme }) => theme.spacing["2"]};
  justify-content: flex-start;
  padding: ${({ theme }) => theme.spacing["3"]};
  font-size: 14px;
`;

const DeselectButton = styled(MDSButton)`
  margin-bottom: ${({ theme }) => theme.spacing["2"]};
`;

const ChromeActionPanelButton = styled(MDSPanelResponsiveButton)`
  & svg {
    font-size: 16px !important;
    height: 16px !important;
    width: 16px !important;
    margin-top: -1px !important;

    color: ${({ theme }) => theme.colors.icon.primary} !important;
  }
`;
